/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useRef } from 'react';
import {
  Button,
  TextTitle,
  TabGroup,
  Tab,
  TabPanel,
  Stepper,
  StepperItem,
  StepperWC,
  Spacer,
  Stack,
} from '@pypestream/design-system';
import { FormProvider } from 'react-hook-form';
import { ConfigPanel } from '../src/config-panel';
import { TabConfig } from './tab-config';
import { TabTasks } from './tab-tasks';
import { TabSupervisor } from './tab-supervisor';
import { TabAgent } from './tab-agent';
import { useSupervisorForm } from './use-supervisor-form';
import { SupervisorKaInfo } from '../../modals/create-solution-modal/supervisor-ka-info';
// import { useRemoveSupervisorModal } from '../../modals/remove-supervisor-modal';

export const SupervisorPanel: React.FC<{
  onClose: () => void;
  viewOnly?: boolean;
  /** Hosted in the header's AI dock, which supplies its own title and close. */
  embedded?: boolean;
  /** The tab to open on (e.g. `knowledge-agent`); the first tab otherwise. */
  initialTab?: string;
}> = ({ onClose, viewOnly, embedded, initialTab }) => {
  const swiperRef = useRef<StepperWC>(null);
  // const [activeTab, setActiveTab] = useState('configuration');

  // const { removeSupervisorModal, openModal: openRemoveSupervisorModal } =
  //   useRemoveSupervisorModal();

  const {
    configForm,
    tasksForm,
    agentForm,
    uploadedFiles,
    addFile,
    removeFile,
    saveSupervisor,
    resetFormState,
    isLoading,
  } = useSupervisorForm();

  const handleSave = () => {
    saveSupervisor(() => {
      onClose();
    });
  };

  const handleCancel = () => {
    resetFormState();
    onClose();
  };

  const hasAgentsConfig = true;

  if (!hasAgentsConfig && !viewOnly)
    return (
      <ConfigPanel
        onClose={onClose}
        // footer={
        //   <>
        //     <Button
        //       className="u-margin-right-auto"
        //       size="large"
        //       variant="ghost"
        //       onClick={() => swiperRef.current?.backStep()}
        //     >
        //       Back
        //     </Button>
        //     <Button
        //       size="large"
        //       onClick={() => {
        //         swiperRef.current?.nextStep();
        //       }}
        //     >
        //       Next
        //     </Button>
        //   </>
        // }
      >
        <div
          style={{
            maxWidth: 500,
            margin: -12,
            overflow: 'hidden',
          }}
        >
          <Stepper id="stepper-agents" ref={swiperRef}>
            <StepperItem name="initial">
              <TextTitle size="xsmall">Agents</TextTitle>
              <Spacer size="large" />
              <SupervisorKaInfo />
              <Spacer size="xlarge" />
              <Stack justifyContent="space-between" slot="footer">
                <Button size="large" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  size="large"
                  onClick={() => {
                    swiperRef.current?.nextStep();
                  }}
                >
                  Add Agents
                </Button>
              </Stack>
            </StepperItem>
            <StepperItem name="configuration">
              <FormProvider {...configForm}>
                <TextTitle size="xsmall">Configure Brand & Model</TextTitle>
                <Spacer size="large" />
                <TabConfig />
              </FormProvider>
              <Stack justifyContent="space-between" slot="footer">
                <Button
                  size="large"
                  variant="ghost"
                  onClick={() => {
                    swiperRef.current?.backStep();
                  }}
                >
                  Back
                </Button>
                <Button
                  size="large"
                  onClick={() => {
                    swiperRef.current?.nextStep();
                  }}
                >
                  Next
                </Button>
              </Stack>
            </StepperItem>
            <StepperItem name="supervisor-agent">
              <FormProvider {...configForm}>
                <TextTitle size="xsmall">Configure Supervisor Agent</TextTitle>
                <Spacer size="large" />
                <TabSupervisor />
              </FormProvider>
              <Stack justifyContent="space-between" slot="footer">
                <Button
                  size="large"
                  variant="ghost"
                  onClick={() => {
                    swiperRef.current?.backStep();
                  }}
                >
                  Back
                </Button>
                <Button
                  size="large"
                  onClick={() => {
                    swiperRef.current?.nextStep();
                  }}
                >
                  Next
                </Button>
              </Stack>
            </StepperItem>
            <StepperItem name="knowledge-agent">
              <FormProvider {...agentForm}>
                <TextTitle size="xsmall">Create a Knowledge Agent</TextTitle>
                <Spacer size="large" />
                <TabAgent
                  uploadedFiles={uploadedFiles}
                  addFile={addFile}
                  removeFile={removeFile}
                />
              </FormProvider>
              <Stack justifyContent="space-between" slot="footer">
                <Button
                  size="large"
                  variant="ghost"
                  onClick={() => {
                    swiperRef.current?.backStep();
                  }}
                >
                  Back
                </Button>
                <Button
                  size="large"
                  onClick={() => {
                    swiperRef.current?.nextStep();
                  }}
                >
                  Next
                </Button>
              </Stack>
            </StepperItem>
            <StepperItem name="tasks">
              <FormProvider {...tasksForm}>
                <TextTitle size="xsmall">Define Tasks</TextTitle>
                <Spacer size="large" />
                <TabTasks />
              </FormProvider>
              <Stack justifyContent="space-between" slot="footer">
                <Button
                  size="large"
                  variant="ghost"
                  onClick={() => {
                    swiperRef.current?.backStep();
                  }}
                >
                  Back
                </Button>
                <Button
                  size="large"
                  onClick={() => {
                    swiperRef.current?.nextStep();
                  }}
                >
                  Save
                </Button>
              </Stack>
            </StepperItem>
          </Stepper>
        </div>
      </ConfigPanel>
    );

  return (
    <>
      <ConfigPanel
        title={embedded ? undefined : 'Agents'}
        footer={
          <>
            {/*
              activeTab === 'configuration' && (
                <Button
                  size="large"
                  variant="danger"
                  onClick={openRemoveSupervisorModal}
                  className="u-margin-right-auto"
                >
                  Remove
                </Button>
              )
            */}
            <Button
              test-id="ps-agents-panel-cancel-btn"
              size="large"
              variant="ghost"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            {!viewOnly && (
              <Button
                test-id="ps-agents-panel-save-btn"
                size="large"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            )}
          </>
        }
        onClose={embedded ? undefined : onClose}
      >
        <TabGroup
          key={initialTab ?? 'default'}
          test-id="ps-agents-panel-tab-group"
          // onTabShow={(e: CustomEvent<{ name: string }>) =>
          //   setActiveTab(e?.detail?.name)
          // }
        >
          <Tab
            slot="tab"
            panel="configuration"
            active={initialTab === 'configuration' || undefined}
          >
            Configuration
          </Tab>
          <Tab
            slot="tab"
            panel="supervisor-agent"
            active={initialTab === 'supervisor-agent' || undefined}
          >
            Solution Name
          </Tab>
          <Tab
            slot="tab"
            panel="knowledge-agent"
            active={initialTab === 'knowledge-agent' || undefined}
          >
            Knowledge Agent
          </Tab>
          <Tab
            slot="tab"
            panel="tasks"
            active={initialTab === 'tasks' || undefined}
          >
            Tasks
          </Tab>
          <TabPanel name="configuration">
            <FormProvider {...configForm}>
              <TabConfig viewOnly={viewOnly} />
            </FormProvider>
          </TabPanel>
          <TabPanel name="supervisor-agent">
            <FormProvider {...configForm}>
              <TabSupervisor viewOnly={viewOnly} />
            </FormProvider>
          </TabPanel>
          <TabPanel name="knowledge-agent">
            <FormProvider {...agentForm}>
              <TabAgent
                uploadedFiles={uploadedFiles}
                addFile={addFile}
                removeFile={removeFile}
                viewOnly={viewOnly}
              />
            </FormProvider>
          </TabPanel>
          <TabPanel name="tasks">
            <FormProvider {...tasksForm}>
              <TabTasks viewOnly={viewOnly} />
            </FormProvider>
          </TabPanel>
        </TabGroup>
      </ConfigPanel>
      {/* {removeSupervisorModal} */}
    </>
  );
};
